import { MILESTONE_COLUMNS, ROLE_DEFINITIONS } from './constants';

function incrementMap(map, key, amount = 1) {
  if (!key) return;
  map.set(key, (map.get(key) || 0) + amount);
}

function mapToRows(map, keyName, valueName = 'project_count') {
  return [...map.entries()]
    .map(([key, value]) => ({ [keyName]: key, [valueName]: value }))
    .sort((a, b) => b[valueName] - a[valueName] || String(a[keyName]).localeCompare(String(b[keyName])));
}

export function computeUploadSummaries(cleanRows) {
  const dashboardByDate = new Map();
  const teamMap = new Map();
  const stateMap = new Map();
  const clientMap = new Map();

  cleanRows.forEach((row) => {
    const date = row.milestone_date;
    if (!date) return;

    const daily = dashboardByDate.get(date) || {
      summary_date: date,
      total_rows: 0,
      unique_projects: new Set(),
      initial_delivered: 0,
      rework_delivered: 0,
    };
    daily.total_rows += 1;
    if (row.project_key) daily.unique_projects.add(row.project_key);
    if (row.milestone_type === 'Initial Project Delivery') daily.initial_delivered += 1;
    if (row.milestone_type === 'Restamp Required') daily.rework_delivered += 1;
    dashboardByDate.set(date, daily);

    ROLE_DEFINITIONS.forEach((role) => {
      const member = row[role.normField];
      if (!member || !row.project_key) return;
      const key = `${date}||${role.key}||${member}`;
      const current = teamMap.get(key) || {
        summary_date: date,
        role_key: role.key,
        role_label: role.label,
        member_name: member,
        project_count: 0,
      };
      current.project_count += 1;
      teamMap.set(key, current);
    });

    if (row.milestone_type === 'Initial Project Delivery' && row.project_key) {
      if (row.state) {
        const key = `${date}||${row.state}`;
        const current = stateMap.get(key) || { summary_date: date, state: row.state, projects: new Set() };
        current.projects.add(row.project_key);
        stateMap.set(key, current);
      }
      if (row.client) {
        const key = `${date}||${row.client}`;
        const current = clientMap.get(key) || { summary_date: date, client: row.client, projects: new Set() };
        current.projects.add(row.project_key);
        clientMap.set(key, current);
      }
    }
  });

  const dashboardSummary = [...dashboardByDate.values()].flatMap((day) => [
    {
      summary_date: day.summary_date,
      metric_key: 'total_rows',
      metric_label: 'Total Rows',
      metric_value: day.total_rows,
      metric_json: {},
    },
    {
      summary_date: day.summary_date,
      metric_key: 'unique_projects',
      metric_label: 'Unique Projects',
      metric_value: day.unique_projects.size,
      metric_json: {},
    },
    {
      summary_date: day.summary_date,
      metric_key: 'initial_delivered',
      metric_label: 'Initial Delivered',
      metric_value: day.initial_delivered,
      metric_json: {},
    },
    {
      summary_date: day.summary_date,
      metric_key: 'rework_delivered',
      metric_label: 'Rework Delivered',
      metric_value: day.rework_delivered,
      metric_json: {},
    },
  ]);

  const teamSummary = [...teamMap.values()].map((item) => ({
    summary_date: item.summary_date,
    role_key: item.role_key,
    role_label: item.role_label,
    member_name: item.member_name,
    project_count: item.project_count,
  }));

  const stateSummary = [...stateMap.values()].map((item) => ({
    summary_date: item.summary_date,
    state: item.state,
    project_count: item.projects.size,
  }));

  const clientSummary = [...clientMap.values()].map((item) => ({
    summary_date: item.summary_date,
    client: item.client,
    project_count: item.projects.size,
  }));

  return { dashboardSummary, teamSummary, stateSummary, clientSummary };
}

export function milestoneTypeToKey(milestoneType) {
  const match = MILESTONE_COLUMNS.find((column) => column.milestoneType === milestoneType);
  return match?.key || 'other';
}

export function groupTeamPerformanceRows(rows = []) {
  return ROLE_DEFINITIONS.map((role) => {
    const byMember = new Map();
    rows.filter((row) => row.role_key === role.key).forEach((row) => {
      const member = row.member_name;
      if (!member) return;
      const key = milestoneTypeToKey(row.milestone_type);
      const current = byMember.get(member) || {
        member_name: member,
        initial_project_delivery: 0,
        restamp_required: 0,
        post_install_letter: 0,
        other: 0,
        total: 0,
      };
      const count = Number(row.project_count || 0);
      current[key] += count;
      current.total += count;
      byMember.set(member, current);
    });

    return {
      ...role,
      rows: [...byMember.values()]
        .sort((a, b) => a.member_name.localeCompare(b.member_name, undefined, { sensitivity: 'base' })),
    };
  });
}

export function buildTeamContributionSummary(groupedRoles = []) {
  const summaryMap = new Map();
  groupedRoles.forEach((role) => {
    role.rows.forEach((row) => {
      const current = summaryMap.get(row.member_name) || {
        member_name: row.member_name,
        total_contribution: 0,
      };
      current[role.key] = row.total;
      current.total_contribution += row.total;
      summaryMap.set(row.member_name, current);
    });
  });

  return [...summaryMap.values()].sort((a, b) => a.member_name.localeCompare(b.member_name, undefined, { sensitivity: 'base' }));
}

export { mapToRows, incrementMap };
